import { api } from './client';

function qs(params?: Record<string, unknown>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (
      value !== undefined &&
      value !== null &&
      (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    ) {
      search.set(key, String(value));
    }
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

export interface ApiResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
  errors?: unknown[];
}

export const houseApi = {
  getCurrent: () => api.get<ApiResponse<unknown>>('/house/current').then((r) => r.data),
  getSettings: () => api.get<ApiResponse<unknown>>('/house/current/settings').then((r) => r.data),
  updateCurrent: (data: unknown) =>
    api.patch<ApiResponse<unknown>>('/house/current', data).then((r) => r.data),
};

export const communityApi = {
  /** Returns full envelope so list pages can read `meta` (pagination/totals). */
  listPeople: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/community/people${qs(params)}`),
  getPerson: (id: string) =>
    api.get<ApiResponse<unknown>>(`/community/people/${id}`).then((r) => r.data),
  createPerson: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/community/people', data).then((r) => r.data),
  updatePerson: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/community/people/${id}`, data).then((r) => r.data),
  changePersonStatus: (id: string, data: unknown) =>
    api.post<ApiResponse<unknown>>(`/community/people/${id}/status`, data).then((r) => r.data),
  /** Projeção mínima (id + nome) para escolher responsáveis. */
  directory: (params?: Record<string, unknown>) =>
    api
      .get<ApiResponse<{ id: string; name: string }[]>>(`/community/directory${qs(params)}`)
      .then((r) => r.data),
  getAccess: (id: string) =>
    api.get<ApiResponse<PersonAccess>>(`/community/people/${id}/access`).then((r) => r.data),
  grantOperator: (id: string) =>
    api
      .post<ApiResponse<PersonAccess>>(`/community/people/${id}/access/operator`)
      .then((r) => r.data),
  revokeOperator: (id: string) =>
    api
      .delete<ApiResponse<PersonAccess>>(`/community/people/${id}/access/operator`)
      .then((r) => r.data),
  resendOperatorInvitation: (id: string) =>
    api
      .post<ApiResponse<{ invitationStatus: string }>>(
        `/community/people/${id}/access/operator/resend`,
        undefined,
        { 'Idempotency-Key': crypto.randomUUID() },
      )
      .then((r) => r.data),
  cancelOperatorInvitation: (id: string) =>
    api
      .post<ApiResponse<{ invitationStatus: string }>>(
        `/community/people/${id}/access/operator/invitation/cancel`,
        undefined,
        { 'Idempotency-Key': crypto.randomUUID() },
      )
      .then((r) => r.data),
};

export interface PersonAccess {
  personId: string;
  hasIdentity: boolean;
  isOperator: boolean;
  isAdmin: boolean;
  roles: string[];
  grantedAt: string | null;
  invitationStatus?: 'pending' | 'activated' | 'expired' | 'cancelled' | 'unknown' | null;
  invitation?: { principalStatus: string; created: boolean } | null;
}

export const calendarApi = {
  listEvents: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/calendar/events${qs(params)}`),
  getEvent: (id: string) =>
    api.get<ApiResponse<unknown>>(`/calendar/events/${id}`).then((r) => r.data),
  createEvent: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/calendar/events', data).then((r) => r.data),
  updateEvent: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/calendar/events/${id}`, data).then((r) => r.data),
};

export const tasksApi = {
  listTasks: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/tasks${qs(params)}`),
  getTask: (id: string) => api.get<ApiResponse<unknown>>(`/tasks/${id}`).then((r) => r.data),
  createTask: (data: unknown) => api.post<ApiResponse<unknown>>('/tasks', data).then((r) => r.data),
  updateTask: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/tasks/${id}`, data).then((r) => r.data),
  changeTaskStatus: (id: string, status: unknown) =>
    api.post<ApiResponse<unknown>>(`/tasks/${id}/status`, { status }).then((r) => r.data),
  assign: (id: string, personId: string) =>
    api.post<ApiResponse<unknown>>(`/tasks/${id}/assignments`, { personId }).then((r) => r.data),
  unassign: (id: string, assignmentId: string) =>
    api
      .delete<ApiResponse<unknown>>(`/tasks/${id}/assignments/${assignmentId}`)
      .then((r) => r.data),
  addChecklistItem: (id: string, label: string) =>
    api.post<ApiResponse<unknown>>(`/tasks/${id}/checklist`, { label }).then((r) => r.data),
  toggleChecklistItem: (id: string, itemId: string, completed: boolean) =>
    api
      .patch<ApiResponse<unknown>>(`/tasks/${id}/checklist/${itemId}`, { completed })
      .then((r) => r.data),
  removeChecklistItem: (id: string, itemId: string) =>
    api.delete<ApiResponse<unknown>>(`/tasks/${id}/checklist/${itemId}`).then((r) => r.data),
};

export const inventoryApi = {
  listItems: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/inventory/items${qs(params)}`),
  getItem: (id: string) =>
    api.get<ApiResponse<unknown>>(`/inventory/items/${id}`).then((r) => r.data),
  createItem: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/inventory/items', data).then((r) => r.data),
  updateItem: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/inventory/items/${id}`, data).then((r) => r.data),
  createMovement: (id: string, data: unknown) =>
    api.post<ApiResponse<unknown>>(`/inventory/items/${id}/movements`, data).then((r) => r.data),
  getLowStock: () =>
    api.get<ApiResponse<unknown[]>>('/inventory/alerts/low-stock').then((r) => r.data),
  listCategories: () =>
    api.get<ApiResponse<unknown[]>>('/inventory/categories').then((r) => r.data),
  createCategory: (name: string) =>
    api.post<ApiResponse<unknown>>('/inventory/categories', { name }).then((r) => r.data),
  listLocations: () => api.get<ApiResponse<unknown[]>>('/inventory/locations').then((r) => r.data),
  createLocation: (name: string) =>
    api.post<ApiResponse<unknown>>('/inventory/locations', { name }).then((r) => r.data),
};

export const financeApi = {
  listEntries: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/finance/entries${qs(params)}`),
  getEntry: (id: string) =>
    api.get<ApiResponse<unknown>>(`/finance/entries/${id}`).then((r) => r.data),
  createEntry: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/finance/entries', data).then((r) => r.data),
  updateEntry: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/finance/entries/${id}`, data).then((r) => r.data),
  changeStatus: (id: string, status: string) =>
    api.post<ApiResponse<unknown>>(`/finance/entries/${id}/status`, { status }).then((r) => r.data),
  cancelEntry: (id: string) =>
    api.post<ApiResponse<unknown>>(`/finance/entries/${id}/cancel`).then((r) => r.data),
  getSummary: () => api.get<ApiResponse<unknown>>('/finance/summary').then((r) => r.data),
  listCategories: () => api.get<ApiResponse<unknown[]>>('/finance/categories').then((r) => r.data),
  createCategory: (name: string, type: string) =>
    api.post<ApiResponse<unknown>>('/finance/categories', { name, type }).then((r) => r.data),
};

export const obligationsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/obligations${qs(params)}`),
  get: (id: string) => api.get<ApiResponse<unknown>>(`/obligations/${id}`).then((r) => r.data),
  create: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/obligations', data).then((r) => r.data),
  update: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/obligations/${id}`, data).then((r) => r.data),
  changeStatus: (id: string, status: unknown) =>
    api.post<ApiResponse<unknown>>(`/obligations/${id}/status`, { status }).then((r) => r.data),
  addResponsible: (id: string, personId: string) =>
    api
      .post<ApiResponse<unknown>>(`/obligations/${id}/responsibles`, { personId })
      .then((r) => r.data),
  removeResponsible: (id: string, responsibleId: string) =>
    api
      .delete<ApiResponse<unknown>>(`/obligations/${id}/responsibles/${responsibleId}`)
      .then((r) => r.data),
};

export const reportsApi = {
  getDashboard: () => api.get<ApiResponse<unknown>>('/reports/dashboard').then((r) => r.data),
  getFinanceSummary: () =>
    api.get<ApiResponse<unknown>>('/reports/finance-summary').then((r) => r.data),
  getOperationalSummary: () =>
    api.get<ApiResponse<unknown>>('/reports/operational-summary').then((r) => r.data),
};

export const documentsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/documents${qs(params)}`),
  get: (id: string) => api.get<ApiResponse<unknown>>(`/documents/${id}`).then((r) => r.data),
  upload: (formData: FormData) =>
    api.post<ApiResponse<unknown>>('/documents', formData).then((r) => r.data),
  update: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/documents/${id}`, data).then((r) => r.data),
  archive: (id: string) =>
    api.post<ApiResponse<unknown>>(`/documents/${id}/archive`).then((r) => r.data),
  download: (id: string) => api.getBlob(`/documents/${id}/download`),
};

export const functionsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/functions${qs(params)}`),
  getById: (id: string) => api.get<ApiResponse<unknown>>(`/functions/${id}`).then((r) => r.data),
  create: (data: unknown) => api.post<ApiResponse<unknown>>('/functions', data).then((r) => r.data),
  update: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/functions/${id}`, data).then((r) => r.data),
  assign: (id: string, personId: string) =>
    api
      .post<ApiResponse<unknown>>(`/functions/${id}/assignments`, { personId })
      .then((r) => r.data),
  unassign: (id: string, assignmentId: string) =>
    api
      .delete<ApiResponse<unknown>>(`/functions/${id}/assignments/${assignmentId}`)
      .then((r) => r.data),
};

export const meApi = {
  dashboard: () => api.get<ApiResponse<unknown>>('/me/dashboard').then((r) => r.data),
  profile: () => api.get<ApiResponse<unknown>>('/me/profile').then((r) => r.data),
  tasks: () => api.get<ApiResponse<unknown>>('/me/tasks').then((r) => r.data),
  calendar: () => api.get<ApiResponse<unknown>>('/me/calendar').then((r) => r.data),
  messages: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/me/messages${qs(params)}`),
  acknowledgeTask: (id: string) =>
    api.post<ApiResponse<unknown>>(`/me/tasks/${id}/acknowledge`).then((r) => r.data),
  startTask: (id: string) =>
    api.post<ApiResponse<unknown>>(`/me/tasks/${id}/start`).then((r) => r.data),
  completeTask: (id: string) =>
    api.post<ApiResponse<unknown>>(`/me/tasks/${id}/complete`).then((r) => r.data),
  declineTask: (id: string, reason?: string) =>
    api.post<ApiResponse<unknown>>(`/me/tasks/${id}/decline`, { reason }).then((r) => r.data),
  confirmAttendance: (eventId: string) =>
    api.post<ApiResponse<unknown>>(`/me/attendance/${eventId}/confirm`).then((r) => r.data),
  declineAttendance: (eventId: string) =>
    api.post<ApiResponse<unknown>>(`/me/attendance/${eventId}/decline`).then((r) => r.data),
  readMessage: (id: string) =>
    api.post<ApiResponse<unknown>>(`/me/messages/${id}/read`).then((r) => r.data),
  acknowledgeMessage: (id: string) =>
    api.post<ApiResponse<unknown>>(`/me/messages/${id}/acknowledge`).then((r) => r.data),
};

export const communicationApi = {
  listMessages: (params?: Record<string, unknown>) =>
    api.get<ApiResponse<unknown[]>>(`/communication/messages${qs(params)}`),
  createMessage: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/communication/messages', data).then((r) => r.data),
};

export const rolesApi = {
  list: () => api.get<ApiResponse<unknown[]>>('/roles'),
  getById: (id: string) => api.get<ApiResponse<unknown>>(`/roles/${id}`).then((r) => r.data),
  create: (data: unknown) => api.post<ApiResponse<unknown>>('/roles', data).then((r) => r.data),
  update: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/roles/${id}`, data).then((r) => r.data),
  setPermissions: (id: string, permissions: string[]) =>
    api.post<ApiResponse<unknown>>(`/roles/${id}/permissions`, { permissions }).then((r) => r.data),
  assign: (roleId: string, personId: string) =>
    api
      .post<ApiResponse<unknown>>(`/roles/${roleId}/assignments`, { personId })
      .then((r) => r.data),
  removeAssignment: (roleId: string, assignmentId: string) =>
    api
      .delete<ApiResponse<void>>(`/roles/${roleId}/assignments/${assignmentId}`)
      .then((r) => r.data),
  getMyPermissions: () =>
    api.get<ApiResponse<string[]>>('/permissions/effective').then((r) => r.data),
};

export const billingApi = {
  overview: () => api.get<ApiResponse<unknown>>('/billing').then((r) => r.data),
  checkout: (body: unknown, idempotencyKey: string) =>
    api
      .post<ApiResponse<unknown>>('/billing/checkout', body, { 'Idempotency-Key': idempotencyKey })
      .then((r) => r.data),
  cancel: (idempotencyKey: string) =>
    api
      .post<ApiResponse<unknown>>('/billing/subscription/cancellation', undefined, {
        'Idempotency-Key': idempotencyKey,
      })
      .then((r) => r.data),
};
